import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuthContext } from '../../context/AuthContext';

interface Payment {
  id: string;
  amount: number;
  totalDueAmount: number;
  status: string;
  dueDate: string;
  invoiceNumber?: string;
  description?: string;
  payabliToken?: string;
  payabliEntryId?: string;
  currency?: string;
  paymentType?: string;
  createdAt?: string;
  updatedAt?: string;
  statementDate?: string;
  paymentDate?: string;
  paymentIdentity?: {
    id: string;
    email: string;
    phone: string | null;
    firstName: string;
    lastName: string;
    uniqueId: string;
    guarantorLastName: string;
  };
}

interface UserInfo {
  name: string;
  gn: string;
  email?: string;
  phone?: string;
  guarantorLastName?: string;
}

interface UserInfoHeaderProps {
  userInfo: UserInfo;
  pendingPaymentsCount: number;
  payments?: any;
  onPayNow?: (amount: number, isScheduled: boolean) => void;
  // New props for the new flow
  paymentIdentityId?: string;
  onLoadPayabliScript?: (amount: number, isScheduled: boolean, scheduledPaymentData?: any) => void;
  isNewFlow?: boolean;
  // Scheduled payment props
  scheduleFrequency?: string;
  setScheduleFrequency?: (value: string) => void;
  splitType?: string;
  setSplitType?: (value: string) => void;
  startDate?: string;
  setStartDate?: (value: string) => void;
  numberOfPayments?: number;
  setNumberOfPayments?: (value: number) => void;
  amountPerPayment?: string;
  setAmountPerPayment?: (value: string) => void;
  endDate?: string;
  isScheduledPaymentValid?: () => boolean;
  getMinStartDate?: () => string;
  SCHEDULE_FREQUENCIES?: any;
  SPLIT_TYPES?: any;
}

// Add frequency mapping constant with proper typing
type FrequencyKey = 'weekly' | 'biweekly' | 'monthly' | 'quarterly';
type ApiFrequency = 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ONETIME';

const FREQUENCY_TO_API_MAP: Record<FrequencyKey, ApiFrequency> = {
  weekly: 'WEEKLY',
  biweekly: 'BIWEEKLY',
  monthly: 'MONTHLY',
  quarterly: 'QUARTERLY'
};

const PAYMENT_TYPE_MAP = {
  scheduled: 'RECURRING',
  oneTime: 'ONE_TIME'
} as const;

const UserInfoHeader: React.FC<UserInfoHeaderProps> = ({
  userInfo,
  pendingPaymentsCount,
  payments = [],
  onPayNow,
  paymentIdentityId,
  onLoadPayabliScript,
  isNewFlow = false,
  scheduleFrequency = 'weekly',
  setScheduleFrequency = () => {},
  splitType = 'equal',
  setSplitType = () => {},
  startDate = '',
  setStartDate = () => {},
  numberOfPayments = 1,
  setNumberOfPayments = () => {},
  amountPerPayment = '',
  setAmountPerPayment = () => {},
  endDate = '',
  isScheduledPaymentValid = () => true,
  getMinStartDate = () => new Date().toISOString().split('T')[0],
  SCHEDULE_FREQUENCIES = { WEEKLY: 'weekly', BIWEEKLY: 'biweekly', MONTHLY: 'monthly', QUARTERLY: 'quarterly' },
  SPLIT_TYPES = { EQUAL: 'equal', MANUAL: 'manual' }
}) => {
  const { theme } = useTheme();
  const { isAuthenticated } = useAuthContext();
  const [amount, setAmount] = useState<number>(0);
  const [isScheduledPayment, setIsScheduledPayment] = useState<boolean>(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [startDateError, setStartDateError] = useState<string>('');
  const [isStartDateValid, setIsStartDateValid] = useState<boolean>(true);

  // Calculate total amount from all payments
  const totalAmount = payments.reduce((sum: number, payment: any) => sum + (payment?.totalDueAmount || 0), 0);

  // Validate start date
  const validateStartDate = useCallback((date: string) => {
    if (!date) {
      setStartDateError('');
      setIsStartDateValid(true);
      return true;
    }

    const selectedDate = new Date(date + 'T00:00:00.000Z');
    const tomorrow = new Date();
    tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
    tomorrow.setUTCHours(0, 0, 0, 0);

    const maxDate = new Date();
    maxDate.setUTCDate(maxDate.getUTCDate() + 31);
    maxDate.setUTCHours(0, 0, 0, 0);

    if (selectedDate < tomorrow) {
      setStartDateError('Start date must be at least tomorrow');
      setIsStartDateValid(false);
      return false;
    }

    if (selectedDate > maxDate) {
      setStartDateError('Start date cannot be more than 30 days from tomorrow');
      setIsStartDateValid(false);
      return false;
    }

    setStartDateError('');
    setIsStartDateValid(true);
    return true;
  }, []);

  // Reset amount when payments change or when component mounts
  useEffect(() => {
    if (totalAmount > 0) {
      setAmount(totalAmount);
    }
  }, [totalAmount]);

  // Validate start date when scheduled payment is toggled or start date changes
  useEffect(() => {
    if (isScheduledPayment && startDate) {
      validateStartDate(startDate);
    } else {
      setStartDateError('');
      setIsStartDateValid(true);
    }
  }, [isScheduledPayment, startDate, validateStartDate]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setAmount(isNaN(value) ? 0 : value);
  };

  const handleScheduledPaymentChange = (checked: boolean) => {
    if (checked && !isAuthenticated) {
      setShowLoginPopup(true);
    } else {
      setIsScheduledPayment(checked);
      // Reset amount if switching to scheduled payment and current amount is less than 10%
      if (checked && amount < (totalAmount * 0.1)) {
        setAmount(totalAmount * 0.1);
      }
    }
  };

  const closeLoginPopup = () => {
    setShowLoginPopup(false);
  };

  const handleLoginRedirect = () => {
    // Save current URL to localStorage
    const currentUrl = window.location.pathname + window.location.search;
    localStorage.setItem('redirectAfterLogin', currentUrl);
    
    // Redirect to login page
    window.location.href = '/login';
    closeLoginPopup();
  };

  // Get tomorrow's date in UTC
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Get 30 days from tomorrow in UTC
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setUTCDate(maxDate.getUTCDate() + 31); // 30 days from tomorrow
    return maxDate.toISOString().split('T')[0];
  };

  const isAmountValid = () => {
    if (amount <= 0) {
      return false;
    }
    
    if (isScheduledPayment) {
      return amount >= (totalAmount * 0.1) && isScheduledPaymentValid() && isStartDateValid;
    }
    return amount > 0 && amount <= totalAmount;
  };

  const handlePayNow = () => {
    if (!isAmountValid()) return;

    // Use total amount if no specific amount is entered
    const finalAmount = amount > 0 ? amount : totalAmount;

    // Prepare scheduled payment data
    const scheduledPaymentData = isScheduledPayment ? {
      isScheduled: true,
      frequency: FREQUENCY_TO_API_MAP[scheduleFrequency as FrequencyKey] || 'WEEKLY',
      paymentType: PAYMENT_TYPE_MAP.scheduled,
      splitType: splitType,
      startDate: startDate,
      endDate: endDate,
      numberOfPayments: numberOfPayments,
      amountPerPayment: amountPerPayment,
    } : {
      isScheduled: false,
      frequency: 'ONETIME',
      paymentType: PAYMENT_TYPE_MAP.oneTime,
    };

    if (isNewFlow && paymentIdentityId && onLoadPayabliScript) {
      // New flow: Use paymentIdentityId and open Payabali popup
      onLoadPayabliScript(finalAmount, isScheduledPayment, scheduledPaymentData);
    } else if (onPayNow) {
      // Old flow: Use existing onPayNow callback
      onPayNow(finalAmount, isScheduledPayment);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6 w-full mt-4">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
        <div className="flex-1 w-full">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">{userInfo.name}</h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              <p><span className="font-medium">Guarantor Number:</span> {userInfo.gn}</p>
              {userInfo.email && <p><span className="font-medium mt-2">Email:</span> {userInfo.email}</p>}
            </div>
            <div>
              {userInfo.guarantorLastName && <p><span className="font-medium mt-2">Guarantor Last Name:</span> {userInfo.guarantorLastName}</p>}
              {userInfo.phone && <p><span className="font-medium mt-2">Phone:</span> {userInfo.phone}</p>}
            </div>
          </div>
          
          {/* Total Amount Display */}
          <div className="mt-4">
            <label className="block text-gray-800 text-sm font-bold mb-3">Total Amount Due</label>
            <label className="block text-gray-600 text-xs font-medium mb-3">
              Or adjust the amount below to make a partial payment
            </label>
            <div className="bg-white rounded-lg px-2 text-gray-800 text-lg font-bold">
              ${totalAmount.toFixed(2)}
            </div>
          </div>
          {/* Scheduled Payment Option */}
          <div className="mb-4 mt-4">
            <label className="scheduled-payment-checkbox flex items-center gap-2">
              <input
                type="checkbox"
                checked={isScheduledPayment}
                onChange={(e) => handleScheduledPaymentChange(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span>Set up scheduled payments</span>
            </label>
          </div>

          {/* Scheduled Payment Form */}
          {isScheduledPayment && (
            <div className="scheduled-payment-form rounded-lg p-3 sm:p-4 mb-4 border border-gray-200 payment-form-animate">
              {/* Frequency Selection */}
              <div className="mb-4">
                <label className="block text-sm font-bold text-gray-700 mb-2">Payment Frequency</label>
                <select
                  value={scheduleFrequency}
                  onChange={(e) => setScheduleFrequency(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={SCHEDULE_FREQUENCIES.WEEKLY}>Weekly</option>
                  <option value={SCHEDULE_FREQUENCIES.BIWEEKLY}>Once in 2 weeks</option>
                  <option value={SCHEDULE_FREQUENCIES.MONTHLY}>Monthly</option>
                </select>
              </div>

              {/* Start Date */}
              <div className="mb-4">
                <label className="block text-sm font-bold text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    validateStartDate(e.target.value);
                  }}
                  min={getTomorrowDate()}
                  max={getMaxDate()}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    startDateError ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {startDateError && (
                  <p className="text-red-500 text-sm mt-1">{startDateError}</p>
                )}
              </div>
            </div>
          )}

          {/* Amount Input */}
          <div className="mt-0">
            <label className="block text-gray-800 text-sm font-bold mb-3">Payment Amount</label>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <input
                max={totalAmount}
                value={amount || ''}
                type="number"
                min={0}
                onChange={handleAmountChange}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                placeholder={`Enter amount (max: $${totalAmount.toFixed(2)})`}
                // @ts-ignore
                onWheel={(e) => e?.target?.blur()}
              />
              <button
                className={`px-6 py-2 rounded-md text-white text-base md:text-lg font-semibold transition-colors whitespace-nowrap ${
                  isAmountValid() 
                    ? `${theme["button-bg"]} hover:opacity-90` 
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
                onClick={handlePayNow}
                disabled={!isAmountValid()}
              >
                {isScheduledPayment ? 'Schedule' : `Pay ${amount > 0 ? `$${amount.toFixed(2)}` : `$${totalAmount.toFixed(2)}`}`}
              </button>
            </div>
            {isScheduledPayment && amount < (totalAmount * 0.1) && (
              <p className="text-red-500 text-sm mt-1">
                Scheduled payments require at least 10% of total amount (${(totalAmount * 0.1).toFixed(2)})
              </p>
            )}
            {amount > totalAmount && (
              <p className="text-red-500 text-sm mt-1">
                Payment amount cannot exceed total amount due
              </p>
            )}
          </div>

          

          {/* Login Popup */}
          {showLoginPopup && (
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
              <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
                <h2 className="text-lg sm:text-xl font-bold mb-4">Login Required</h2>
                <p className="mb-6 text-sm sm:text-base">You need to login to schedule payments.</p>
                <div className="flex justify-end space-x-3">
                  <button 
                    onClick={closeLoginPopup}
                    className="px-3 sm:px-4 py-2 border border-gray-300 rounded-md text-sm sm:text-base text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleLoginRedirect}
                    className={`px-3 sm:px-4 py-2 rounded-md text-white text-sm sm:text-base ${theme["button-bg"]}`}
                  >
                    Login
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* <div className="text-right ml-6">
          <p className="text-sm text-gray-500">Total Pending Payments</p>
          <p className={`text-2xl font-bold ${theme["text-600"]}`}>{pendingPaymentsCount}</p>
        </div> */}
      </div>
    </div>
  );
};

export default UserInfoHeader; 